import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {delay} from 'recoil-toolkit';

import {useQueryClient} from '@tanstack/react-query';
import {PutFiveStarsParams} from 'domain/usecases';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {makePutFiveStars} from 'main/factories/usecases';
import {
	FiveStarsRatingEnum,
	HomeEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {useAdditionalQuestionsErrorStore} from '../stores/useAdditionalQuestionsErrorStore';
import {useRatingDetailCodeStore} from '../stores/useRatingDetailCodeStore';
import {useRatingErrorStore} from '../stores/useRatingErrorStore';
import {useRatingModalStore} from '../stores/useRatingModalStore';
import {Params} from '../stores/useRatingDetailCodeStore/types';

export function useRatingModal() {
	const navigate = useNavigate();

	const [operationState, setOperationState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);
	const ratingModal = useRatingModalStore(state => state.ratingModal);
	const setRatingModal = useRatingModalStore(state => state.setRatingModal);
	const setRatingDetailCode = useRatingDetailCodeStore(state => state.setRatingDetailCode);
	const setRatingError = useRatingErrorStore(state => state.setRatingError);
	const setAdditionalQuestionsError = useAdditionalQuestionsErrorStore(
		state => state.setAdditionalQuestionsError,
	);
	const queryClient = useQueryClient();
	const isSubmitting = operationState === OperationStateTypesEnum.SUBMITTING;

	async function handleRefuseToEvaluate(data: PutFiveStarsParams) {
		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			await makePutFiveStars().put(data);
			await delay(1000);
			await Promise.resolve(setRatingModal('stale'));

			setOperationState(OperationStateTypesEnum.DONE);

			queryClient.resetQueries({queryKey: [QUERY_KEYS.FIVE_STARS]});
			navigate(HomeEnum.HOME_PAGE);
			setRatingModal('evaluate');
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			setRatingModal('error-refuse');
		}
	}

	function handleGoToEvaluationPage({cdAtendimento, tipo}: Params) {
		setRatingError(false);
		setAdditionalQuestionsError(false);
		setRatingModal('stale');
		setRatingDetailCode({cdAtendimento, tipo});
		navigate(FiveStarsRatingEnum.FIVESTARS_RATING, {state: {cdAtendimento, tipo}});
	}

	function handleOpenRefuseToEvaluateModal({cdAtendimento, tipo}: Params) {
		setRatingModal('refuse');
		setRatingDetailCode({cdAtendimento, tipo});
	}

	return {
		handleRefuseToEvaluate,
		handleGoToEvaluationPage,
		isSubmitting,
		ratingModal,
		handleOpenRefuseToEvaluateModal,
	};
}
